import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';
import axios from 'axios';
import SEO from '@/components/SEO';
import { OrganizationSchema, WebSiteSchema } from '@/components/JsonLd';
import { 
  GraduationCap, Users, BarChart3, Zap, CheckCircle, ArrowRight,
  BookOpen, Award, Building2, Bell, FileText, MapPin, Briefcase, ChevronRight,
  User, UsersRound, Sparkles, MousePointerClick, Play,
  Crosshair, Search, MessageSquare, Database, Linkedin, Instagram
} from 'lucide-react';

const ICON_MAP = {
  FileText, Sparkles, MousePointerClick, Bell, Play,
  Crosshair, Search, MessageSquare, Database, CheckCircle,
  GraduationCap, Users, BarChart3, Zap, BookOpen, Award, Building2,
  User, UsersRound, Briefcase, MapPin,
};

const SEEKER_ICONS = [FileText, Sparkles, MousePointerClick, Bell, Play];
const EMPLOYER_ICONS = [Crosshair, Sparkles, Search, MessageSquare, Database];

const LandingPage = () => {
  const { user } = useAuth();
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [featuredFallback, setFeaturedFallback] = useState(false);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/featured/jobs`).then(res => {
      setFeaturedJobs(res.data.jobs || []);
      setFeaturedFallback(!!res.data.is_fallback);
    }).catch(() => {});
    axios.get(`${API}/settings/public`).then(res => setCms(res.data)).catch(() => {});
  }, []);

  const relativeTime = (iso) => {
    if (!iso) return '';
    try {
      const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
      if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))}w ago`;
      return `${Math.floor(diff / (86400 * 30))}mo ago`;
    } catch { return ''; }
  };

  const heroBadge = cms?.hero_badge || "India's Premium Education Job Portal";
  const heroTitle = cms?.hero_title || "Hire the Best";
  const heroHighlight = cms?.hero_highlight || "Educators.";
  const heroDescription = cms?.hero_description || "The recruitment platform built exclusively for education. Connect schools with qualified teachers. AI-powered, certified-focused, built for Indian education.";
  const stats = cms?.stats || [
    { value: "100+", label: "Schools & Institutions" },
    { value: "10,000+", label: "Educators Registered" },
  ];
  const whyTitle = cms?.why_edassist_title || "Why Choose EdAssist?";
  const whySubtitle = cms?.why_edassist_subtitle || "Streamline your job search with AI-powered tools & Hire top education talent with intelligent matching";
  const seekerFeatures = cms?.job_seeker_features || [
    "AI-powered Resume Parser for onboarding",
    "AI-powered job recommendations tailored to you",
    "One-click applications—no long forms",
    "Real-time alerts & updates on job status",
    "Personalize hiring with video resumes",
  ];
  const employerFeatures = cms?.employer_features || [
    "Job Posting & Built-in ATS",
    "AI-powered candidate matching engine",
    "Smart filters by subject, grade, and experience",
    "Chat with candidates directly from dashboard",
    "High-Quality Education-Specific Database",
  ];
  const ctaTitle = cms?.cta_title || "Ready to Transform Education Hiring?";
  const ctaSubtitle = cms?.cta_subtitle || "Join 500+ schools and 10,000+ educators already on EdAssist.";
  const footerTagline = cms?.footer_tagline || "India's trusted job board for educators, teachers, and academic professionals.";
  const copyrightText = cms?.copyright_text || "2025 EdAssist. All rights reserved.";
  const socialLinkedin = cms?.social_linkedin || '';
  const socialInstagram = cms?.social_instagram || '';

  const features = [
    { icon: GraduationCap, title: "Education-Focused", description: "Purpose-built for schools, colleges & EdTech. Filter by subject, grade level, and board." },
    { icon: FileText, title: "AI Resume Parsing", description: "Automatically extract certifications, teaching experience & qualifications from resumes." },
    { icon: Users, title: "Talent Pipeline", description: "Visual kanban boards with demo lesson scheduling to track candidates through hiring." },
    { icon: Bell, title: "Smart Job Alerts", description: "Educators get notified instantly when matching positions are posted." },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={null}
        description="India's premium education job portal. Find teaching jobs, build your resume, and connect with top schools and institutions."
        path="/"
      />
      <OrganizationSchema />
      <WebSiteSchema />
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/edassist-logo.png" alt="EdAssist" className="h-8" />
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/jobs" className="text-slate-600 hover:text-slate-900 transition-colors">Browse Jobs</Link>
              <Link to="/resume-builder" className="text-slate-600 hover:text-slate-900 transition-colors">Resume Builder</Link>
              <Link to="/training" className="text-slate-600 hover:text-slate-900 transition-colors">Training</Link>
              <Link to="/blogs" className="text-slate-600 hover:text-slate-900 transition-colors">Blog</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">For Institutions</Link>
            </nav>
            <div className="flex items-center gap-3">
              {user ? (
                <Link to="/dashboard"><Button data-testid="dashboard-btn">Dashboard</Button></Link>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" data-testid="login-btn">Log in</Button></Link>
                  <Link to="/register"><Button data-testid="get-started-btn">Get Started</Button></Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient text-white py-20 lg:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm">
                <Award className="w-4 h-4 text-yellow-400" />
                <span data-testid="hero-badge">{heroBadge}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: 'Outfit' }} data-testid="hero-title">
                {heroTitle}<br />
                <span className="text-indigo-400">{heroHighlight}</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-300 max-w-xl leading-relaxed" data-testid="hero-description">
                {heroDescription}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-8" data-testid="hero-cta-btn">
                    <Building2 className="w-5 h-5 mr-2" />For Schools
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8" data-testid="browse-jobs-btn">
                    <BookOpen className="w-5 h-5 mr-2" />For Educators
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/10" data-testid="hero-stats">
                {stats.map((stat, idx) => (
                  <div key={idx}>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-2xl blur-3xl"></div>
                <img src="https://static.prod-images.emergentagent.com/jobs/da6381f4-c24d-4a49-b4fa-8c9703750625/images/9ff5da54a1d06a960c6e40ccaebcd381735e1f53d61946119819951074a46fb2.jpeg" alt="Indian classroom with teacher and students" className="relative rounded-2xl shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Jobs Banner */}
      {featuredJobs.length > 0 && (
        <section className="py-6 bg-white border-b border-slate-100 overflow-hidden" data-testid="featured-jobs-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
                {featuredFallback ? 'Latest Jobs' : 'Featured Jobs'}
              </h3>
              <div className="h-px flex-1 bg-slate-200"></div>
              <Link to="/jobs" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></Link>
            </div>
          </div>
          <div className="featured-scroll">
            <div className="featured-scroll-inner">
              {[...featuredJobs, ...featuredJobs].map((job, idx) => (
                <Link key={`${job.job_id}-${idx}`} to={`/jobs/${job.job_id}`} className="flex-shrink-0 w-72 bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all group">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Briefcase className="w-5 h-5 text-indigo-600" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-semibold text-slate-900 text-sm truncate group-hover:text-indigo-600 transition-colors">{job.title}</p>
                        {job.is_featured && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-0 uppercase tracking-wide flex-shrink-0">★</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{job.organization?.name || job.company_name || 'Institution'}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3 h-3" />{job.location}</span>
                        {job.posted_at && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">· {relativeTime(job.posted_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* For Educators Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <LBadge className="bg-green-100 text-green-800 mb-4">For Educators</LBadge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                Find Your Dream Teaching Position
              </h2>
              <ul className="space-y-4">
                {[
                  { icon: BookOpen, text: "Filter by subject, grade level & board (CBSE, ICSE, IB)" },
                  { icon: Bell, text: "Get instant alerts when matching jobs are posted" },
                  { icon: FileText, text: "AI extracts your B.Ed, TET, CTET from resume" },
                  { icon: Award, text: "Showcase your certifications & demo videos" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><item.icon className="w-4 h-4 text-green-600" /></div>
                    <span className="text-slate-700">{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="inline-block mt-8">
                <Button size="lg">Create Educator Profile<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
            <div className="relative">
              <img src="https://static.prod-images.emergentagent.com/jobs/da6381f4-c24d-4a49-b4fa-8c9703750625/images/248a00f9489e919caeccc05c4f3ff39cac4fd9ae115d2fafe930756871da341b.jpeg" alt="Indian educator" className="rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* For Schools Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img src="https://static.prod-images.emergentagent.com/jobs/da6381f4-c24d-4a49-b4fa-8c9703750625/images/5b325c112b43023c9c2db94c22c507f4a24a0c128ae9cb22871c74291c67fdb6.jpeg" alt="Modern Indian school campus" className="rounded-2xl shadow-xl" />
            </div>
            <div className="order-1 lg:order-2">
              <LBadge className="bg-indigo-100 text-indigo-800 mb-4">For Schools & Institutions</LBadge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                Hire Certified Teachers Faster
              </h2>
              <ul className="space-y-4">
                {[
                  { icon: Users, text: "Access pool of 10,000+ verified educators" },
                  { icon: GraduationCap, text: "Filter by certifications: B.Ed, CTET, TET scores" },
                  { icon: BarChart3, text: "Track candidates with visual ATS pipeline" },
                  { icon: Zap, text: "Schedule demo lessons directly in platform" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><item.icon className="w-4 h-4 text-indigo-600" /></div>
                    <span className="text-slate-700">{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="inline-block mt-8">
                <Button size="lg">Register Your School<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Built for Education Hiring</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Every feature designed specifically for schools, colleges, and educators.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-6 card-hover animate-fadeIn" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4"><feature.icon className="w-6 h-6 text-indigo-600" /></div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose EdAssist - CMS DRIVEN */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="why-edassist-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }} data-testid="why-edassist-title">
              {whyTitle}
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto" data-testid="why-edassist-subtitle">
              {whySubtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* For Job Seekers */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="why-job-seekers">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center"><User className="w-6 h-6 text-indigo-600" /></div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>For Job Seekers</h3>
                  <p className="text-sm text-slate-500">Streamline your job search with AI-powered tools</p>
                </div>
              </div>
              <div className="mt-6 space-y-1">
                {seekerFeatures.map((text, idx) => {
                  const Icon = SEEKER_ICONS[idx % SEEKER_ICONS.length];
                  return (
                    <div key={idx} className="flex items-center gap-4 py-3 border-l-2 border-indigo-200 pl-4 hover:border-indigo-500 transition-colors">
                      <div className="w-9 h-9 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-indigo-500" /></div>
                      <span className="text-sm text-slate-700">{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* For Employers */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="why-employers">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center"><UsersRound className="w-6 h-6 text-slate-700" /></div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>For Employers</h3>
                  <p className="text-sm text-slate-500">Hire top education talent with intelligent matching</p>
                </div>
              </div>
              <div className="mt-6 space-y-1">
                {employerFeatures.map((text, idx) => {
                  const Icon = EMPLOYER_ICONS[idx % EMPLOYER_ICONS.length];
                  return (
                    <div key={idx} className="flex items-center gap-4 py-3 border-l-2 border-slate-200 pl-4 hover:border-slate-500 transition-colors">
                      <div className="w-9 h-9 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-slate-600" /></div>
                      <span className="text-sm text-slate-700">{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - CMS DRIVEN */}
      <section className="py-20 hero-gradient text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit' }} data-testid="cta-title">{ctaTitle}</h2>
          <p className="text-lg text-slate-300 mb-8" data-testid="cta-subtitle">{ctaSubtitle}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register"><Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-8">Get Started Free<ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
            <Link to="/pricing"><Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8">View Pricing</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4"><img src="/edassist-logo.png" alt="EdAssist" className="h-8 w-auto object-contain brightness-0 invert" /></div>
              <p className="text-sm" data-testid="footer-tagline">{footerTagline}</p>
              <p className="text-xs text-slate-500 mt-2">EdAssist.co.in</p>
              {(socialLinkedin || socialInstagram) && (
                <div className="flex items-center gap-3 mt-4">
                  {socialLinkedin && (
                    <a href={socialLinkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-indigo-600 transition-colors" data-testid="footer-linkedin">
                      <Linkedin className="w-4 h-4 text-white" />
                    </a>
                  )}
                  {socialInstagram && (
                    <a href={socialInstagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-pink-600 transition-colors" data-testid="footer-instagram">
                      <Instagram className="w-4 h-4 text-white" />
                    </a>
                  )}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/jobs" className="hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/training" className="hover:text-white transition-colors">Training Programs</Link></li>
                <li><Link to="/resume-builder" className="hover:text-white transition-colors">Resume Builder</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/blogs" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/ambassador" className="hover:text-white transition-colors">Campus Ambassador</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link></li>
                <li><Link to="/fraud-alert" className="hover:text-white transition-colors text-red-400 hover:text-red-300">Fraud Alert</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-sm" data-testid="copyright">
            &copy; {new Date().getFullYear()} EdAssist (HRassistance India Consultancy LLP). All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const LBadge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${className}`}>
    {children}
  </span>
);

export default LandingPage;
